// Website copy supplied in WebsiteContent1.docx.
export type WebsiteContentBlock =
    | { type: "paragraph"; text: string }
    | { type: "heading"; text: string }
    | { type: "list"; items: string[] };

export const coursesOverview: { heading: string; blocks: WebsiteContentBlock[] } = {
    "heading": "Develop your teaching practice independently",
    "blocks": [
        {
            "type": "paragraph",
            "text": "On this page, you can explore a range of interactive self-study products for teachers at different stages of their professional development."
        },
        {
            "type": "paragraph",
            "text": "All products have been designed by me, Olha Popivnenko, under my personal teacher-training brand, Triad Teacher Training. I am a CELTA and DELTA trainer, English teacher, and product, syllabus, curriculum and materials designer. You can find more detailed information about my background, qualifications and professional experience in the About section."
        },
        {
            "type": "paragraph",
            "text": "Through this page, you can view a detailed description of each product and purchase the one that best matches your needs and current stage of development."
        },
        {
            "type": "heading",
            "text": "What the products include"
        },
        {
            "type": "paragraph",
            "text": "Every product available on this website consists of a series of interactive self-study lessons. In addition to written input from me as a teacher trainer, the lessons include a variety of interactive activities, such as:"
        },
        {
            "type": "list",
            "items": [
                "gap-fill tasks",
                "matching activities",
                "multiple-choice questions",
                "reflection logs",
                "short-answer tasks",
                "tests and quizzes",
                "self-check activities"
            ]
        },
        {
            "type": "paragraph",
            "text": "The exact structure and content will vary depending on the product. On each individual product page, you will find a detailed description of:"
        },
        {
            "type": "list",
            "items": [
                "what the product covers",
                "who it is designed for",
                "the areas of teaching practice it focuses on",
                "what is included in each section or lesson"
            ]
        },
        {
            "type": "paragraph",
            "text": "Please read the product description carefully before purchasing to make sure the product is suitable for your needs and level of professional development."
        },
        {
            "type": "heading",
            "text": "Before you purchase"
        },
        {
            "type": "paragraph",
            "text": "Please note that you will need to create an account on the website before purchasing a product. Your purchase will be linked directly to your account, and this is how you will access the product after payment."
        },
        {
            "type": "heading",
            "text": "What happens after purchase"
        },
        {
            "type": "paragraph",
            "text": "Once your payment has been completed successfully, the purchased product will automatically appear in your account on the Home page. You can begin the product immediately or return to it and complete it at any time that is convenient for you. Your product access, progress and any saved results will remain linked to your account."
        }
    ]
};

export const servicesOverview: { heading: string; blocks: WebsiteContentBlock[] } = {
    "heading": "Live online support for your next professional step",
    "blocks": [
        {
            "type": "paragraph",
            "text": "On this page, you can explore the options available for individual online teacher development, consultation services, DELTA and CELTA preparation, lesson observations, English lessons, and one-to-one teacher training support."
        },
        {
            "type": "paragraph",
            "text": "All services are provided directly by me, Olha Popivnenko. I am a CELTA and DELTA trainer, English teacher, and product, syllabus, curriculum and materials designer. You can find more detailed information about my background, qualifications and professional experience in the About section."
        },
        {
            "type": "paragraph",
            "text": "Through this page, you can view a detailed description of each service I offer and book the one that best matches your needs."
        },
        {
            "type": "heading",
            "text": "How to book"
        },
        {
            "type": "paragraph",
            "text": "When viewing any service, you will find a Book button at the bottom of the page."
        },
        {
            "type": "paragraph",
            "text": "Once you click it, a short booking form will appear. You will be asked to provide your basic personal and contact details, including your preferred method of communication. Depending on the service you are booking, you will also be asked a few questions about your background, goals, needs, and what you would like support with. Once you have completed the form, simply click Submit."
        },
        {
            "type": "heading",
            "text": "What happens after you submit the form"
        },
        {
            "type": "paragraph",
            "text": "Your completed form will be sent directly to me by email. I will review it and contact you personally within 24 hours using the communication method you selected."
        },
        {
            "type": "paragraph",
            "text": "From there, we will discuss the service in more detail and work around your individual needs. You will have the opportunity to ask any questions you may have, clarify what you would like to focus on, and agree on the practical details."
        },
        {
            "type": "paragraph",
            "text": "We will then arrange a suitable time or schedule, whether this is for a one-off consultation, lesson observation, English lessons, or ongoing CELTA or DELTA preparation. Live sessions usually take place via Zoom or Google Meet."
        },
        {
            "type": "paragraph",
            "text": "Payment for any service is arranged directly with me after we have discussed the details and confirmed the schedule or delivery plan."
        }
    ]
};

export const aboutContent: { introduction: string; qualifications: string[]; experience: WebsiteContentBlock[]; mindset: WebsiteContentBlock[]; connect: string } = {
    "introduction": "Hey, I’m Olha. I’m a teacher trainer, curriculum designer, materials developer, and English teacher, and I am the creator and provider of every product you’ll find on this website.",
    "qualifications": [
        "Registered CELTA Trainer, since 2025",
        "Full DELTA, London, Distinction in Teaching",
        "CELTA, Liverpool, Pass A",
        "MA Linguistics, University of Manchester, Distinction",
        "MSc Psychology, University of Liverpool, Merit",
        "BSc Computer Science, University of Liverpool, First-Class Honours",
        "BSc Artificial Intelligence, Kharkiv National University of Radio Electronics, Ukraine",
        "IELTS, Manchester, 8.5",
        "Cambridge C2 Proficiency (CPE), London, Grade A"
    ],
    "experience": [
        {
            "type": "heading",
            "text": "CELTA Trainer"
        },
        {
            "type": "paragraph",
            "text": "I have been working as a CELTA Trainer on full-time, face-to-face products in Liverpool since 2025. During this time, I have designed the full set of input sessions that I deliver as part of the product and assessed multiple lessons and assignments."
        },
        {
            "type": "heading",
            "text": "DELTA Module 1 Trainer"
        },
        {
            "type": "paragraph",
            "text": "I have worked as an independent online DELTA Module 1 trainer since 2025. During this time, I have prepared a number of candidates who successfully passed the exam, including candidates who achieved Merit and Distinction."
        },
        {
            "type": "heading",
            "text": "DELTA Module 3 Teaching"
        },
        {
            "type": "paragraph",
            "text": "I have been supervising DELTA Module 3 candidates since 2026. My main areas of supervision are:"
        },
        {
            "type": "list",
            "items": [
                "Teaching Exam Classes",
                "English for Academic Purposes",
                "Teaching One-to-One",
                "English for Specific Purposes"
            ]
        },
        {
            "type": "heading",
            "text": "Triad Teacher Training"
        },
        {
            "type": "paragraph",
            "text": "I am the founder of Triad Teacher Training, a teacher-training brand under which I have designed and delivered a number of online group products in teaching methodology, including Core Methodology Product, Advanced Methodology Product, and IELTS Methodology Product."
        },
        {
            "type": "heading",
            "text": "Curriculum & Materials Design"
        },
        {
            "type": "paragraph",
            "text": "Alongside teacher training, I have worked as a curriculum and materials designer since 2025. I have designed products, syllabuses, programmes and teaching materials for several large online language schools in Ukraine, Italy and Germany."
        },
        {
            "type": "paragraph",
            "text": "My experience includes both the design of complete products and programmes, including General English and exam-preparation products, as well as the development of individual bespoke teaching materials for specific learner groups, levels and educational contexts."
        },
        {
            "type": "heading",
            "text": "Teaching English"
        },
        {
            "type": "paragraph",
            "text": "In my teaching career, I have increasingly specialised in higher-level learners, English for teachers, and exam preparation, including IELTS and Cambridge exams such as FCE, CAE and CPE. My teaching experience includes both face-to-face and online teaching, group and one-to-one classes, and working in both monolingual and multilingual environments."
        }
    ],
    "mindset": [
        {
            "type": "heading",
            "text": "The sky is the limit"
        },
        {
            "type": "paragraph",
            "text": "I went from obtaining my first teaching qualification, CELTA, to becoming a CELTA Trainer in England in just a year and a half. By the age of 22, I had also completed four university degrees. My professional development has been unusually fast, and I am likely among the youngest CELTA trainers to have started their trainer career at this stage of life."
        },
        {
            "type": "paragraph",
            "text": "The path was not always straightforward. There were moments of resistance, doubt from other people, a lack of support, and situations where the easier option would have been to wait, slow down, or accept that something was “too early” or unrealistic."
        },
        {
            "type": "paragraph",
            "text": "I kept going because I believed in my own ability even when other people did not. That is why I genuinely believe in aiming for things that may initially look impossible. For me, “the sky is the limit” is not a motivational phrase. It is a principle I have tested in my own career."
        },
        {
            "type": "heading",
            "text": "I will never be afraid of someone else’s ambition"
        },
        {
            "type": "paragraph",
            "text": "I promised myself that I would become the kind of trainer who is not afraid of ambition, and who knows how to work with high achievers."
        },
        {
            "type": "paragraph",
            "text": "My own journey involved more sleepless nights than I could count, more books than I could ever properly list, and plenty of things I chose to give up because my goals mattered more to me. Now, most of that feels worth it. The sacrifices have largely been overshadowed by what they allowed me to achieve."
        },
        {
            "type": "paragraph",
            "text": "But one thing never faded. I still remember what it felt like when other people were intimidated by the scale of my ambitions, my goals, and my desire for more. Instead of supporting them, they tried to reduce them. To make them sound unrealistic. To convince me to keep both feet firmly on the ground."
        },
        {
            "type": "paragraph",
            "text": "Back then, I made myself a promise. One day, I would be the person carrying the title of trainer. I would be the person others came to with their goals, ambitions, doubts, and plans. And I would be the kind of trainer who could work with different kinds of people, including high achievers. The kind who helps ambitious people turn their goals into something real. The kind who makes ambition grow rather than shrink. The kind who does not try to extinguish someone’s drive simply because it is bigger than what feels comfortable to other people."
        },
        {
            "type": "heading",
            "text": "Discipline beats talent. Obsession beats both."
        },
        {
            "type": "paragraph",
            "text": "At this point, teacher training, English teaching, product design, curriculum development and materials writing are not simply ways for me to earn a living. They are areas I am genuinely passionate about and enjoy spending my time on."
        },
        {
            "type": "paragraph",
            "text": "I am happy to spend my free time learning something new, taking another product, reading another book, or developing another skill. I will never pass up an opportunity to ask questions of professionals who are more experienced than I am."
        },
        {
            "type": "paragraph",
            "text": "And I will never release a product thinking, “That’ll do.” I can think that’ll do about what I am wearing. I can think that’ll do and leave the house without makeup. I can think that’ll do and leave the dishes in the sink until the morning. But I will never think “that’ll do” when I am creating something under my own name and brand."
        },
        {
            "type": "paragraph",
            "text": "Absolute perfection does not exist. There will always be more to learn, another perspective to consider, another improvement that becomes possible with greater experience. But there is a standard I can control. I will not put something into the world under my brand until I can look at it and genuinely say:"
        },
        {
            "type": "paragraph",
            "text": "This is the best I can make it with the knowledge, skills, experience and resources I have today."
        }
    ],
    "connect": "Photos of all my qualifications and certificates, as well as my journey from teacher to teacher trainer, can be found on my social media."
};

export const courseUseContent: { title: string; blocks: WebsiteContentBlock[] } = {
    "title": "Product use & intellectual property",
    "blocks": [
        {
            "type": "paragraph",
            "text": "This product and all materials included in it are the intellectual property of Olha Popivnenko and are protected by copyright."
        },
        {
            "type": "paragraph",
            "text": "By purchasing this product, you receive a personal, non-transferable licence to access and use the product materials for your own learning and professional development."
        },
        {
            "type": "heading",
            "text": "You may:"
        },
        {
            "type": "list",
            "items": [
                "access and complete the product for your own personal use",
                "make personal notes based on the product content",
                "apply the knowledge, ideas and techniques you learn in your own teaching practice",
                "refer back to the product materials for your own professional development",
                "discuss the product with other people in general terms"
            ]
        },
        {
            "type": "paragraph",
            "text": "You are welcome to talk publicly about your experience of taking this product."
        },
        {
            "type": "heading",
            "text": "You may:"
        },
        {
            "type": "list",
            "items": [
                "mention that you are taking or have completed the product",
                "share your personal impressions, opinions and reviews",
                "talk generally about the product structure, organisation and your learning experience",
                "share your product completion certificate publicly",
                "tag Olha Popivnenko on social media",
                "share links to the product or website",
                "recommend the product to other teachers",
                "share brief, incidental screenshots where the purpose is to show that you are taking the product or to discuss your experience"
            ]
        },
        {
            "type": "paragraph",
            "text": "Any screenshots shared publicly must not reveal substantial product content, complete activities, answer keys, downloadable materials, or enough material for another person to use the product without purchasing access."
        },
        {
            "type": "heading",
            "text": "You may not:"
        },
        {
            "type": "list",
            "items": [
                "share your account, login details or product access with another person",
                "copy, reproduce, distribute, forward or publicly upload parts of the product",
                "share complete activities, answer keys, downloadable resources, lesson content or other product materials in a way that gives others access to the product",
                "upload substantial parts of the product to social media, file-sharing platforms, public repositories, websites, forums, shared drives or similar services",
                "resell, sublicense or otherwise commercially redistribute the product or any part of it",
                "incorporate substantial parts of the product into your own paid products, workshops, teacher-training programmes, publications, memberships or digital products",
                "present the product materials, wording, activities, frameworks or resources as your own work",
                "adapt, repackage or reproduce the product in a substantially similar form for commercial purposes",
                "use the materials to create a competing or substantially similar commercial product"
            ]
        },
        {
            "type": "paragraph",
            "text": "By continuing to the product, you confirm that you have read, understood and agreed to these Product Use & Intellectual Property conditions."
        }
    ]
};
